import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Menu as MenuIcon } from '@mui/icons-material';
import logo from '../../assets/logo.png';
import { ROUTES } from '../../constants/routes';

const NAV_ITEMS = [
  { label: 'Features', path: ROUTES.FEATURES },
  { label: 'Pricing', path: ROUTES.PRICING },
  { label: 'Directory', path: ROUTES.DIRECTORY },
  { label: 'FAQ', path: ROUTES.FAQ },
  { label: 'Blog', path: ROUTES.BLOG },
  { label: 'Affiliate', path: '/affiliate' },
];

const SharedHeader = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const isActivePath = (path) => location.pathname === path;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(10, 10, 10, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src={logo}
              alt="Logo"
              style={{ height: 32, marginRight: 12 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Nexus
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.65rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  mt: '2px',
                }}
              >
                by FutureSense
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  color: isActivePath(item.path) ? '#ffffff' : theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff'
                  }
                }}
              >
                {item.label}
              </Button>
            ))}

            <Box sx={{ width: 1, height: 20, borderLeft: '1px solid rgba(255, 255, 255, 0.1)', mx: 1 }} />

            <Button
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: '0.85rem',
                '&:hover': { color: '#ffffff' }
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(ROUTES.SIGNUP)}
              sx={{
                ml: 1,
                background: '#ffffff',
                color: '#000000',
                borderRadius: 2,
                px: 3,
                fontWeight: 700,
                fontSize: '0.85rem',
                '&:hover': {
                  background: '#f0f0f0',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Join Free
            </Button>
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(true)}
            sx={{ display: { xs: 'block', md: 'none' }, color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 250,
            background: alpha(theme.palette.common.black, 0.95),
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <Box sx={{ pt: 2 }}>
          <List>
            {NAV_ITEMS.map((item) => (
              <ListItem
                key={item.path}
                button
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                sx={{
                  backgroundColor: isActivePath(item.path) ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    color: isActivePath(item.path) ? theme.palette.primary.main : 'white',
                    '& .MuiTypography-root': {
                      fontWeight: isActivePath(item.path) ? 600 : 400
                    }
                  }}
                />
              </ListItem>
            ))}
            <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.1) }} />
            <ListItem button onClick={() => { navigate(ROUTES.LOGIN); setMobileMenuOpen(false); }}>
              <ListItemText primary="Login" sx={{ color: 'white' }} />
            </ListItem>
            <ListItem button onClick={() => { navigate(ROUTES.SIGNUP); setMobileMenuOpen(false); }}>
              <ListItemText primary="Sign Up" sx={{ color: 'white' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default SharedHeader;
